import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '@/stores/authStore';
import type { NameDisplayFormat } from '@shared/types/tenant-config';

export type { NameDisplayFormat };

/**
 * Format a name according to the specified display format.
 * Handles empty/whitespace strings gracefully.
 */
export function formatName(
  firstName: string,
  lastName: string,
  format: NameDisplayFormat = 'first_last'
): string {
  const first = firstName?.trim() || '';
  const last = lastName?.trim() || '';

  // Handle edge cases
  if (!first && !last) return '';
  if (!first) return last;
  if (!last) return first;

  switch (format) {
    case 'last_first':
      return `${last}, ${first}`;
    case 'last_first_no_comma':
      return `${last} ${first}`;
    case 'first_last':
    default:
      return `${first} ${last}`;
  }
}

/**
 * Hook to get name formatting functions using tenant config.
 * Uses useShallow for performance (only re-renders when format values change).
 * Returns stable function references via useCallback.
 */
export function useNameFormat() {
  const { staffNameFormat, studentNameFormat, parentNameFormat } = useAuthStore(
    useShallow((state) => ({
      staffNameFormat: state.tenant?.settings?.staffNameFormat ?? 'first_last',
      studentNameFormat: state.tenant?.settings?.studentNameFormat ?? 'first_last',
      parentNameFormat: state.tenant?.settings?.parentNameFormat ?? 'first_last',
    }))
  );

  const formatStaffName = useCallback(
    (firstName: string, lastName: string) => formatName(firstName, lastName, staffNameFormat),
    [staffNameFormat]
  );

  const formatStudentName = useCallback(
    (firstName: string, lastName: string) => formatName(firstName, lastName, studentNameFormat),
    [studentNameFormat]
  );

  const formatParentName = useCallback(
    (firstName: string, lastName: string) => formatName(firstName, lastName, parentNameFormat),
    [parentNameFormat]
  );

  return { formatStaffName, formatStudentName, formatParentName };
}
